import React from 'react'
import mixpanel from 'mixpanel-browser'
import { useAuthUser } from './AuthUserProvider'
import { MixpanelEvent, MixpanelEventProperties } from 'types/types'

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || ''
const APP_ENV = process.env.REACT_APP_ENV || 'local'

// Initialized at module scope (like loadStripe in App.tsx) rather than in a useEffect:
// React runs effects children-first, so a provider effect would run *after* the mount
// effects of the components that track, and those early events would be lost.
// Wrapped in try/catch because this runs inside App.tsx's import graph, before any error
// boundary - a throw here (malformed persisted cookie, storage denied) would cost the
// whole app rather than one event.
if (MIXPANEL_TOKEN !== '') {
  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      api_host: 'https://api-eu.mixpanel.com',
      autocapture: false,
      track_pageview: false,
      // The SDK defaults to an insecure cookie scoped to .locumloop.com, which would send
      // a stable identifier along with every request to the backend. The app is
      // single-origin, so neither default is needed.
      // Seems to work fine with http://localhost:3000 though.
      secure_cookie: true,
      cross_subdomain_cookie: false,
    })
  } catch (e) {
    console.error('mixpanel init failed', e)
  }
}

interface IMixpanelContext {
  track: (event: MixpanelEvent, properties?: MixpanelEventProperties) => void
}

const MixpanelContext = React.createContext<IMixpanelContext>({} as IMixpanelContext)

const MixpanelProvider: React.FC = ({ children }) => {
  const { authUser } = useAuthUser()

  const track = React.useCallback((event: MixpanelEvent, properties?: MixpanelEventProperties) => {
    if (MIXPANEL_TOKEN === '') return
    // Tracking must never break the funnel it observes: several call sites sit inside the
    // try block of a submit handler, where a throw would show a generic error to a user
    // whose account was already created.
    try {
      // env is spread in here rather than registered as a super property so that
      // mixpanel.reset() on logout cannot wipe it.
      mixpanel.track(event, { env: APP_ENV, application: 'frontend', ...properties })
    } catch (e) {
      console.error('mixpanel track failed', event, e)
    }
  }, [])

  // authUser is tri-state: undefined = still loading, null = signed out, User = signed in.
  // We act on the transition, not the current value, so that anonymous visitors (null on
  // every page load) don't get reset() and lose their $device_id.
  React.useEffect(() => {
    if (MIXPANEL_TOKEN === '') return
    if (authUser === undefined) return  // still loading, decide nothing

    try {
      const currentId = authUser?.firebaseId ?? null
      // The SDK's own persisted $user_id is the single source of truth for "who are we
      // currently identified as". A component-level ref would be null on every page load,
      // so a cold load carrying a previous user's identity cookie would read as "no
      // transition" and attribute the next visitor's events to that user.
      // $user_id is written by identify() and cleared by reset(), both synchronously, so
      // this is accurate within a page session too. It assumes identity is only ever
      // established through identify(); alias() would set distinct_id without $user_id and
      // slip past this guard (src/ never calls alias()).
      const identifiedId: string | null = mixpanel.get_property('$user_id') ?? null
      if (currentId === identifiedId) return  // no transition

      // Leaving an identified state - including one established in a previous page session
      // per the cookie - resets first, so the next user's events don't land on the old
      // profile. Skipped on anonymous -> identified, which preserves the $device_id so
      // mixpanel can merge the pre-signup events into the new user.
      if (identifiedId) mixpanel.reset()
      if (currentId) mixpanel.identify(currentId)
    } catch (e) {
      console.error('mixpanel identify failed', e)
    }
  }, [authUser])

  const contextValue = { track }
  return (
    <MixpanelContext.Provider value={contextValue}>
      {children}
    </MixpanelContext.Provider>
  )
}

function useMixpanel() {
  const context = React.useContext(MixpanelContext)
  if (context === undefined) {
    throw new Error('useMixpanel must be used within a MixpanelProvider')
  }
  return context
}

export { MixpanelProvider, useMixpanel }
